import React, { useMemo } from 'react'
import { Alert, Col, Icon, List as ListAntd, Row, Tag, Tooltip } from 'antd'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { IntegrationType, ResumeHotel } from 'src/types'

import { ApplicationState } from 'src/store'
import { Button } from 'src/components/Ant'
import CurrencyMask from 'src/components/Formik/InputCurrency/mask'

import { HotelImageUrl } from '../utils'
import { OfflineOfferWarning, WrapListItem } from './styles'

interface Props {
  setSelectedItem: React.Dispatch<React.SetStateAction<ResumeHotel>>
  setVisibleModal: React.Dispatch<React.SetStateAction<boolean>>
}

function SinglePeriod({
  setSelectedItem,
  setVisibleModal
}: Props): JSX.Element {
  const {
    hotels: { searches, currentPeriodKey }
  } = useSelector((state: ApplicationState) => ({
    hotels: state.hotels
  }))

  const { t } = useTranslation()
  const haveLoaded = useMemo(
    () =>
      Boolean(
        Object.keys(searches).length &&
          Object.values(searches).every(({ loading }) => !loading)
      ),
    [searches]
  )

  const filteredHotels = useMemo<ResumeHotel[]>(() => {
    if (haveLoaded) {
      const baseSearch = searches[currentPeriodKey]
      if (baseSearch && !baseSearch.loading) {
        return baseSearch.list
          .filter(item => {
            if (
              baseSearch.selectionFilters &&
              baseSearch.selectionFilters.name
            ) {
              return item.brand_name
                .toLocaleUpperCase()
                .includes(baseSearch.selectionFilters.name)
            }
            return true
          })
          .filter(({ quarter }) =>
            baseSearch.selectionFilters.quarter.some(({ checked }) => checked)
              ? baseSearch.selectionFilters.quarter.find(
                  ({ title }) => title === quarter
                )?.checked
              : true
          )
      }
    }
    return []
  }, [currentPeriodKey, haveLoaded, searches])

  const hasError = useMemo(
    () => Object.values(searches).find(({ error }) => error)?.error || '',
    [searches]
  )

  if (hasError) {
    return <Alert closable={false} message={hasError} type="error" />
  }

  const locale = {
    emptyText: (
      <>
        <Icon type="info-circle" style={{ fontSize: 28, paddingBottom: 4 }} />
        <br />
        {t('web.services.hotels.search-not-found')}
      </>
    )
  }

  return (
    <ListAntd
      data-test-id="hotel-result-list"
      dataSource={filteredHotels}
      itemLayout="vertical"
      locale={locale}
      renderItem={(item: ResumeHotel, index: number) => {
        const has_offline_offer = +item.request_quote_hotel_rooms.some(
          ({ integration_type }) => integration_type === IntegrationType.OFFLINE
        )
        const { total_value, tax_value } = item.request_quote_hotel_rooms?.[0]
        const totalValue = !has_offline_offer
          ? total_value
          : total_value + tax_value

        return (
          <WrapListItem
            data-test-id={`hotel-result-tab-item-${index}`}
            has_offline_offer={has_offline_offer}
            key={item.brand_name}
          >
            <ListAntd.Item.Meta
              avatar={
                <div
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    height: 200,
                    justifyContent: 'center',
                    width: 200
                  }}
                >
                  <HotelImageUrl
                    url={item.hotel_image_url}
                    style={{ maxHeight: 200, maxWidth: 200 }}
                  />
                </div>
              }
              title={item.brand_name}
              description={
                <Row gutter={24}>
                  <Col span={15}>
                    {item.address}
                    <br />
                    {has_offline_offer ? (
                      <OfflineOfferWarning>
                        {t('web.services.hotels.offline-offer-hotel')}
                        <br />
                      </OfflineOfferWarning>
                    ) : null}
                    <br />
                    <span>
                      <Icon type="star" style={{ marginRight: 8 }} />
                      {`${item.category_stars} estrelas`}
                    </span>
                  </Col>
                  <Col offset={1} span={8}>
                    <Tooltip
                      title={
                        CurrencyMask({
                          formatStr: true,
                          value: totalValue
                        }).maskedValue
                      }
                    >
                      <Tag
                        color="green"
                        style={{
                          fontSize: 20,
                          marginBottom: '16px',
                          padding: 4,
                          textAlign: 'center',
                          width: '100%'
                        }}
                      >
                        {`BRL ${
                          CurrencyMask({
                            formatStr: true,
                            value: totalValue
                          }).maskedValue
                        }`}
                      </Tag>
                    </Tooltip>
                    <Button
                      data-test-id={`hotel-result-list-action-${index}`}
                      block
                      onClick={() => {
                        setSelectedItem(item)
                        setVisibleModal(true)
                      }}
                      title="common.actions.select"
                      type="primary"
                    />
                  </Col>
                </Row>
              }
            />
          </WrapListItem>
        )
      }}
    />
  )
}

export default SinglePeriod
